/**
 * AI Voice Script Generator — client interactive.
 *
 * Link-magnet tool: marketer picks industry + tone + hours + services, we
 * call Claude via /api/tools/voice-script-generator and render five editable
 * script sections (greeting, fallback, after-hours, intent detection, sample
 * flow) with one-click copy-to-clipboard.
 *
 * Empty state: pre-rendered HVAC/Plumbing sample script so Googlebot sees
 * real script copy before any interaction.
 */
'use client';

import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Check, Copy, Sparkles } from 'lucide-react';
import { industries } from '@/lib/industries';
import { pushEvent } from '@/lib/analytics/core';

type Tone = 'Friendly' | 'Professional' | 'Energetic' | 'Calm';

interface ScriptSections {
  greeting: string;
  fallback: string;
  afterHours: string;
  intentDetection: string;
  sampleFlow: string;
  source?: 'claude' | 'fallback';
}

const TONES: Tone[] = ['Friendly', 'Professional', 'Energetic', 'Calm'];

// Pre-rendered sample so the page isn't empty on first load.
const SAMPLE_HVAC: ScriptSections = {
  greeting:
    "Thanks for calling Ace Heating & Plumbing — this is your AI dispatch assistant. Is this an emergency, or can I help you book a service?",
  fallback:
    "Sorry, I didn't quite catch that. Are you calling about a leak, no heat, no AC, or booking a regular service?",
  afterHours:
    "You've reached Ace Heating & Plumbing after hours. If this is a plumbing or HVAC emergency, I can dispatch our on-call tech right now — otherwise, I'll book you a first-available slot tomorrow. Which is it?",
  intentDetection:
    "Route by intent: (1) EMERGENCY — no heat, burst pipe, gas smell → page on-call tech immediately. (2) NEW BOOKING → qualify service type + urgency + address, then book. (3) EXISTING JOB → look up ticket, transcribe update. (4) QUOTE REQUEST → collect scope + photos via SMS. (5) BILLING → transfer to office during hours, take callback after hours.",
  sampleFlow:
    "1. Warm greeting with business name + 'emergency or service?'\n2. If emergency: confirm address and callback number, page on-call tech, stay on line.\n3. If service: capture name, property address, and problem description.\n4. Ask when they need it by (today / this week / flexible).\n5. Offer the first three available slots.\n6. Confirm, read back appointment time + tech name.\n7. Send SMS confirmation with calendar invite.\n8. Thank the caller and close.",
};

interface FormState {
  industry: string;
  businessName: string;
  tone: Tone;
  hours: string;
  services: string;
}

const INITIAL_FORM: FormState = {
  industry: 'HVAC & Plumbing',
  businessName: '',
  tone: 'Friendly',
  hours: 'Mon-Fri 9am-5pm',
  services: '',
};

interface SectionCardProps {
  heading: string;
  body: string;
  loading: boolean;
  sectionKey: string;
  onCopy: (key: string, body: string) => void;
  copiedKey: string | null;
}

function SectionCard({ heading, body, loading, sectionKey, onCopy, copiedKey }: SectionCardProps) {
  const isCopied = copiedKey === sectionKey;
  return (
    <div className="rounded-2xl bg-dark-card border border-dark-border p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <h3 className="text-lg font-semibold text-dark-text">{heading}</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onCopy(sectionKey, body)}
          disabled={loading || !body}
          aria-label={`Copy ${heading}`}
          className="shrink-0"
        >
          {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span className="ml-1.5">{isCopied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[95%]" />
          <Skeleton className="h-4 w-[85%]" />
        </div>
      ) : (
        <p className="text-dark-muted leading-relaxed whitespace-pre-line text-sm sm:text-base">
          {body}
        </p>
      )}
    </div>
  );
}

function toErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong generating your script. Please try again.';
}

export default function VoiceScriptGenerator() {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [script, setScript] = useState<ScriptSections>(SAMPLE_HVAC);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const industryOptions = useMemo(() => industries.map((i) => i.name), []);

  const updateField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleCopy = useCallback((key: string, body: string) => {
    if (!body || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(body).then(
      () => {
        setCopiedKey(key);
        pushEvent('voice_script_copied', { section: key });
        window.setTimeout(() => setCopiedKey(null), 1800);
      },
      () => {
        setError('Could not access clipboard. Try copying manually.');
      },
    );
  }, []);

  const handleGenerate = useCallback(async () => {
    setError(null);
    if (!form.businessName.trim()) {
      setError('Please enter your business name.');
      return;
    }
    if (!form.services.trim()) {
      setError('Please list at least one service so we can tailor the script.');
      return;
    }
    setLoading(true);
    pushEvent('voice_script_generate_clicked', {
      industry: form.industry,
      tone: form.tone,
    });
    try {
      const res = await fetch('/api/tools/voice-script-generator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(
          res.status === 429
            ? 'Too many requests — try again in a minute.'
            : `Generation failed (${res.status}). ${text.slice(0, 120)}`,
        );
      }
      const raw: unknown = await res.json();
      if (typeof raw !== 'object' || raw === null) {
        throw new Error('Unexpected response from generator.');
      }
      const obj = raw as Record<string, unknown>;
      const next: ScriptSections = {
        greeting: typeof obj.greeting === 'string' ? obj.greeting : SAMPLE_HVAC.greeting,
        fallback: typeof obj.fallback === 'string' ? obj.fallback : SAMPLE_HVAC.fallback,
        afterHours: typeof obj.afterHours === 'string' ? obj.afterHours : SAMPLE_HVAC.afterHours,
        intentDetection:
          typeof obj.intentDetection === 'string' ? obj.intentDetection : SAMPLE_HVAC.intentDetection,
        sampleFlow: typeof obj.sampleFlow === 'string' ? obj.sampleFlow : SAMPLE_HVAC.sampleFlow,
        source: obj.source === 'claude' || obj.source === 'fallback' ? obj.source : undefined,
      };
      setScript(next);
      pushEvent('voice_script_generated', {
        industry: form.industry,
        tone: form.tone,
        source: next.source ?? 'unknown',
      });
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [form]);

  return (
    <div className="grid lg:grid-cols-5 gap-8 items-start">
      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleGenerate();
        }}
        className="lg:col-span-2 bg-dark-card border border-dark-border rounded-2xl p-6 sm:p-8 space-y-5"
      >
        <div className="flex items-center gap-2 text-primary-400 text-sm font-semibold uppercase tracking-wider">
          <Sparkles className="h-4 w-4" />
          Customize your script
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="vsg-industry">Industry</Label>
          <select
            id="vsg-industry"
            value={form.industry}
            onChange={(e) => updateField('industry', e.target.value)}
            className="h-10 w-full rounded-lg border border-dark-border bg-transparent px-3 text-sm text-dark-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {industryOptions.map((name) => (
              <option key={name} value={name} className="bg-dark-card text-dark-text">
                {name}
              </option>
            ))}
          </select>
        </div>

        <Input
          id="vsg-business-name"
          label="Business name"
          placeholder="e.g. Ace Heating & Plumbing"
          value={form.businessName}
          onChange={(e) => updateField('businessName', e.target.value)}
          maxLength={120}
        />

        <div className="flex flex-col gap-1.5">
          <Label>Tone</Label>
          <div className="grid grid-cols-2 gap-2">
            {TONES.map((t) => {
              const active = form.tone === t;
              return (
                <Button
                  key={t}
                  type="button"
                  variant={active ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updateField('tone', t)}
                  aria-pressed={active}
                >
                  {t}
                </Button>
              );
            })}
          </div>
        </div>

        <Input
          id="vsg-hours"
          label="Business hours"
          placeholder="Mon-Fri 9am-5pm"
          value={form.hours}
          onChange={(e) => updateField('hours', e.target.value)}
          maxLength={160}
        />

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="vsg-services">Services offered</Label>
          <textarea
            id="vsg-services"
            value={form.services}
            onChange={(e) => updateField('services', e.target.value)}
            placeholder="Emergency plumbing, AC repair, furnace install, water heater replacement..."
            rows={4}
            maxLength={1200}
            className="w-full rounded-lg border border-dark-border bg-transparent px-3 py-2 text-sm text-dark-text placeholder:text-dark-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 resize-none"
          />
          <p className="text-xs text-dark-muted">
            One per line or comma-separated. More detail = better script.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-500/40 bg-red-500/5 px-3 py-2 text-sm text-red-400"
          >
            {error}
          </div>
        )}

        <Button type="submit" size="lg" loading={loading} className="w-full">
          {loading ? 'Generating…' : 'Generate Script'}
        </Button>

        <p className="text-xs text-dark-muted">
          Powered by Claude Sonnet. Your inputs aren&apos;t stored — each
          generation is stateless.
        </p>
      </form>

      {/* Script output */}
      <div className="lg:col-span-3 space-y-4">
        <SectionCard
          heading="Opening greeting"
          body={script.greeting}
          loading={loading}
          sectionKey="greeting"
          onCopy={handleCopy}
          copiedKey={copiedKey}
        />
        <SectionCard
          heading="Fallback / clarification"
          body={script.fallback}
          loading={loading}
          sectionKey="fallback"
          onCopy={handleCopy}
          copiedKey={copiedKey}
        />
        <SectionCard
          heading="After-hours handling"
          body={script.afterHours}
          loading={loading}
          sectionKey="afterHours"
          onCopy={handleCopy}
          copiedKey={copiedKey}
        />
        <SectionCard
          heading="Intent detection & routing"
          body={script.intentDetection}
          loading={loading}
          sectionKey="intentDetection"
          onCopy={handleCopy}
          copiedKey={copiedKey}
        />
        <SectionCard
          heading="Sample call flow"
          body={script.sampleFlow}
          loading={loading}
          sectionKey="sampleFlow"
          onCopy={handleCopy}
          copiedKey={copiedKey}
        />
      </div>
    </div>
  );
}
